import React, { createContext, useContext, useState, useCallback } from 'react';
import { DISPLAY_MODES, formatDateTime, formatDate, formatTime, getDisplayModeLabel } from '../utils/timezone';

const TimezoneContext = createContext();

export function TimezoneProvider({ children }) {
  // Initialize from localStorage or default to 'local'
  const [displayMode, setDisplayMode] = useState(() => {
    const saved = localStorage.getItem('timezoneDisplayMode');
    return saved && Object.values(DISPLAY_MODES).includes(saved)
      ? saved
      : DISPLAY_MODES.LOCAL;
  });

  // Update display mode and persist to localStorage
  const setTimezoneDisplayMode = useCallback((mode) => {
    if (Object.values(DISPLAY_MODES).includes(mode)) {
      setDisplayMode(mode);
      localStorage.setItem('timezoneDisplayMode', mode);
    }
  }, []);

  // Cycle through display modes: local -> utc -> practice -> local
  const cycleDisplayMode = useCallback(() => {
    setDisplayMode(current => {
      let next;
      switch (current) {
        case DISPLAY_MODES.LOCAL:
          next = DISPLAY_MODES.UTC;
          break;
        case DISPLAY_MODES.UTC:
          next = DISPLAY_MODES.PRACTICE;
          break;
        case DISPLAY_MODES.PRACTICE:
        default:
          next = DISPLAY_MODES.LOCAL;
          break;
      }
      localStorage.setItem('timezoneDisplayMode', next);
      return next;
    });
  }, []);

  const value = {
    displayMode,
    setDisplayMode: setTimezoneDisplayMode,
    cycleDisplayMode,
    DISPLAY_MODES,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

/**
 * Hook to access timezone context
 * @returns {Object} Timezone context value
 */
export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

/**
 * Hook to get formatted date/time functions bound to current display mode
 * @param {string} practiceTimezone - IANA timezone for the current practice
 * @returns {Object} Formatting functions
 */
export function useFormattedDateTime(practiceTimezone) {
  const { displayMode } = useTimezone();

  return {
    formatDateTime: (dateInput, options) =>
      formatDateTime(dateInput, displayMode, practiceTimezone, options),
    formatDate: (dateInput) =>
      formatDate(dateInput, displayMode, practiceTimezone),
    formatTime: (dateInput) =>
      formatTime(dateInput, displayMode, practiceTimezone),
    getLabel: () => getDisplayModeLabel(displayMode, practiceTimezone),
    displayMode,
  };
}
